import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux"
import { ShipmentSelect } from "./ShipmentSelect";
import { getListPO, ShipmentsLoadingKeys } from "../../slices/shippingSlice";
import { arrayHasItems, getTheHighestCountTag } from "../../helpers/common";
import { LoaderSpinner } from "../LoaderSpinner";
import { DEFAULT_COUNT_BOXES } from "../../consts/common";
import { useClickOutsideBlock } from "../../helpers/useClickOutside";
import axios from "axios";

const resolveUnitsPerBox = (product) => {
  const cartonQty = Number(product?.carton_qty);
  if (Number.isFinite(cartonQty) && cartonQty > 0) {
    return cartonQty;
  }
  const unitsPerBox = Number(product?.unitsPerBox);
  if (Number.isFinite(unitsPerBox) && unitsPerBox > 0) {
    return unitsPerBox;
  }
  const upbFromTag = getTheHighestCountTag(product?.tags || [], "upb");
  if (upbFromTag) {
    return upbFromTag;
  }
  return DEFAULT_COUNT_BOXES;
};

export const CreateShipping = ({
  handleSubmit,
  setIsModalOpen,
  testId,
  testData,
  preSelectedPOId
}) => {
  const dispatch = useDispatch();
  const [poItems, setPOItems] = useState([]);
  const [selectedPOId, setSelectedPOId] = useState(preSelectedPOId || '');
  const [selectedItems, setSelectedItems] = useState({});
  const [isLoadingPOData, setIsLoadingPOData] = useState(false);
  const {
    POs,
    loadingKeys: poLoadingKeys
  } = useSelector((state) => state.shipments)

  const currentPOs = testId && testData?.POs ? testData.POs : POs;
  const listPOsIsLoading = () => testId && testData?.loading ? testData.loading : poLoadingKeys.includes(ShipmentsLoadingKeys.listPOS);
  const isLoadingPOs = listPOsIsLoading();

  const createButtonIsDisabled = 
    !arrayHasItems(poItems) 
    || !Object.keys(selectedItems || {}) 
    || !selectedPOId 
    || poItems.every((item) => Number(item.unitsPerBox || 0) <= 0)
    || listPOsIsLoading();

  const modalRef = useRef(null);
  const handleClose = () => {
    if (testId) {
      console.log("Cancel form!")
      return
    }
    setIsModalOpen(false)
  }
  useClickOutsideBlock(modalRef, handleClose);

  useEffect(() => {
    if (testId) return
    dispatch(getListPO({}));
  }, [])

  // Auto-select PO if preSelectedPOId is provided
  useEffect(() => {
    if (!preSelectedPOId || testId || isLoadingPOData) return;
    
    const loadPOData = async () => {
      // Don't load if we already have items for this PO
      if (selectedPOId === preSelectedPOId && poItems.length > 0) return;
      
      setIsLoadingPOData(true);
      
      try {
        // First, check if PO is in the currentPOs list
        const foundPO = currentPOs.find((po) => po.value === preSelectedPOId);
        
        if (foundPO && foundPO.products && foundPO.products.length > 0) {
          // PO found in list, use it
          const products = foundPO.products || [];
          setSelectedPOId(preSelectedPOId);

          const preparedPoItems = products.map((product) => {
            return {
              id: product.id,
              poId: preSelectedPOId,
              sku: product.sku,
              productName: product.name,
              unitsPerBox: resolveUnitsPerBox(product),
              barcode: product.barcode || '',
              quantity: product.quantity,
              quantityReceived: product.quantityReceived || 0,
            }
          });

          setPOItems(preparedPoItems);
          const initialSelectedItems = preparedPoItems.reduce((acc, item) => {
            acc[item.sku] = true;
            return acc;
          }, {});
          setSelectedItems(initialSelectedItems);
        } else if (!isLoadingPOs) {
          // PO list finished loading but PO not found (or has no products), fetch it directly from shipments endpoint
          try {
            const { data: shipmentsList } = await axios.get(`/api/v1/shipments/list`);
            const foundPO = shipmentsList.find((po) => po.value === preSelectedPOId);
            
            if (foundPO && foundPO.products && foundPO.products.length > 0) {
              const products = foundPO.products || [];
              setSelectedPOId(preSelectedPOId);

              const preparedPoItems = products.map((product) => {
                return {
                  id: product.id,
                  poId: preSelectedPOId,
                  sku: product.sku,
                  productName: product.name,
                  unitsPerBox: resolveUnitsPerBox(product),
                  barcode: product.barcode || '',
                  quantity: product.quantity,
                  quantityReceived: product.quantityReceived || 0,
                }
              });

              setPOItems(preparedPoItems);
              const initialSelectedItems = preparedPoItems.reduce((acc, item) => {
                acc[item.sku] = true;
                return acc;
              }, {});
              setSelectedItems(initialSelectedItems);
            }
          } catch (error) {
            console.error('Failed to fetch PO data:', error);
          }
        }
      } finally {
        setIsLoadingPOData(false);
      }
    };

    loadPOData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preSelectedPOId, currentPOs.length, isLoadingPOs])

  const handlePOChange = (selectedPOId) => {
    const selectedPO = currentPOs.find((shipment) => shipment.value === selectedPOId);
    const products = selectedPO.products || [];
    setSelectedPOId(selectedPOId);

    const preparedPoItems = products.map((product) => {
      return {
        id: product.id,
        poId: selectedPOId,
        sku: product.sku,
        productName: product.name,
        unitsPerBox: resolveUnitsPerBox(product),
        barcode: product.barcode,
        quantity: product.quantity,
        quantityReceived: product.quantityReceived,
      }
    });

    setPOItems(preparedPoItems);

    // // Initialize all items as selected
    const initialSelectedItems = preparedPoItems.reduce((acc, item) => {
      acc[item.sku] = true;
      return acc;
    }, {});
    setSelectedItems(initialSelectedItems);
  };

  // Add this new function to handle checkbox changes
  const handleCheckboxChange = (sku) => {
    setSelectedItems(prev => ({
      ...prev,
      [sku]: !prev[sku]
    }));
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...poItems];
    updatedItems[index][field] = value;
    setPOItems(updatedItems);
  };

  const onSubmit = (e) => {
    if (testId) {
      console.log("Submit form!")
      return
    }
    e.preventDefault();
    const boxesData = poItems.filter((item) => selectedItems[item.sku]).filter((item) => Number(item.unitsPerBox || 0) > 0);
    handleSubmit(boxesData, selectedPOId);
  }

  return (
    <div className="fixed z-10 inset-0 overflow-y-auto" data-testid={testId}>
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        <div ref={modalRef} className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full sm:p-6">
          <div className="sm:flex sm:items-start">
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
              {listPOsIsLoading() ? (
                <LoaderSpinner colorSpinner="black" width="70" height="70" border="6" testId={testId ? `loader-${testId}` : undefined} />
              ) : (
                <>
                  <h3 className="text-lg leading-6 font-medium text-primary mb-4">Create New Shipment</h3>
                  <form onSubmit={onSubmit} className="space-y-4">
                    {!preSelectedPOId && (
                      <div className="flex flex-col gap-2">
                        <label htmlFor="poNumber" className="block text-sm font-medium text-gray-700">
                          P.O. Number
                        </label>
                        <ShipmentSelect 
                          options={currentPOs} 
                          testId={testId ? `select-${testId}` : undefined}
                          selectedOption={selectedPOId}
                          handleChange={handlePOChange}
                        />
                      </div>
                    )}
                    {preSelectedPOId && selectedPOId && (
                      <div className="flex flex-col gap-2">
                        <label htmlFor="poNumber" className="block text-sm font-medium text-gray-700">
                          P.O. Number
                        </label>
                        <div className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-50 text-gray-900 text-sm">
                          {currentPOs.find(po => po.value === selectedPOId)?.title || selectedPOId}
                        </div>
                      </div>
                    )}

                    {selectedPOId && (
                      <div className="mt-4">
                        <table className="min-w-full divide-y divide-gray-200 table-fixed">
                          <thead className="bg-gray-50">
                            <tr>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8 max-w-8">
                                Select
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40 max-w-40">
                                SKU
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-72 max-w-72">
                                Product Name
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24 max-w-24">
                                Order Quantity
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24 max-w-24">
                                Units per Box
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-36 max-w-36">
                                Barcode
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {poItems.map((item, index) => (
                              <tr key={`${item.sku}_${index}`}>
                                <td className="px-6 py-4 w-8 max-w-8">
                                  <input
                                    type="checkbox"
                                    data-testid={testId ? `select-product-${testId}` : undefined}
                                    checked={selectedItems[item.sku]}
                                    onChange={() => handleCheckboxChange(item.sku)}
                                    className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                  />
                                </td>
                                <td className="px-6 py-4 w-40 max-w-40 text-sm font-medium text-gray-900">
                                  {item.sku}
                                </td>
                                <td className="px-6 py-4 w-72 max-w-72 text-sm text-gray-500">
                                  {item.productName}
                                </td>
                                <td className="px-6 py-4 w-16 max-w-16 text-sm text-gray-500">
                                  {item.quantity}
                                </td>
                                <td className="px-6 py-4 w-24 max-w-24 text-sm text-gray-500">
                                  <input
                                    type="number"
                                    data-testid={testId ? `select-unitperbox-${testId}` : undefined}
                                    name={`unitsPerBox-${item.sku}`}
                                    value={item.unitsPerBox}
                                    onChange={(e) => handleItemChange(index, 'unitsPerBox', e.target.value)}
                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-1 px-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    tabIndex={index * 2 + 1}
                                  />
                                </td>
                                <td className="px-6 py-4 w-36 max-w-36 text-sm text-gray-500">
                                  {item.barcode}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                      <button
                        type="submit"
                        disabled={createButtonIsDisabled}
                        data-testid={testId ? `submit-${testId}` : undefined}
                        className="w-full disabled:bg-slate-200 inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:ml-3 sm:w-auto sm:text-sm"
                        tabIndex={poItems.length * 2 + 1}
                      >
                        Create Shipment
                      </button>
                      <button
                        type="button"
                        data-testid={testId ? `cancel-${testId}` : undefined}
                        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:w-auto sm:text-sm"
                        onClick={handleClose}
                        tabIndex={poItems.length * 2 + 2}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}